// src/components/Investment/InvestmentCTA2.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./InvestmentCTA2.css";
import { useLanguage } from "../../contexts/LanguageContext";
import { FaUser, FaUserTie } from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { createCheckoutSession, getStripe } from '../../utils/stripeConfig';
import useAuthListener from '../../hooks/useAuthListener';
import { auth, googleProvider, appleProvider } from '../../firebase/firebase';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';

/* ---- Flag assets (static imports) ---- */
import aeFlag from "../../assets/images/flags/ae_flag.png";
import usFlag from "../../assets/images/flags/us_flag.png";
import frFlag from "../../assets/images/flags/fr_flag.png";
import lbFlag from "../../assets/images/flags/lb_flag.svg";
import qaFlag from "../../assets/images/flags/qa_flag.svg";
import saFlag from "../../assets/images/flags/saudi_flag.webp";
import syFlag from "../../assets/images/flags/syria_flag.webp";

/* Prefer ISO -> image, then currency code -> image */
const ISO_FLAG_MAP = {
  AE: aeFlag,
  US: usFlag
  // FR: frFlag,
  // LB: lbFlag,
  // QA: qaFlag,
  // SA: saFlag,
  // SY: syFlag,
};
const CODE_FLAG_MAP = {
  AED: aeFlag,
  USD: usFlag
};

/* normalize odd values */
const NORMALIZE_MAP = {
  AED: "AED", "UAE DIRHAM": "AED", "AED ": "AED", "د.إ": "AED",
  USD: "USD", "US DOLLAR": "USD", "U.S. DOLLAR": "USD", "US$": "USD", "$": "USD",
  // EUR: "EUR", EURO: "EUR", "€": "EUR",
  // QAR: "QAR", "QATARI RIYAL": "QAR",
  // SAR: "SAR", RIYAL: "SAR", "SAUDI RIYAL": "SAR",
  // LBP: "LBP", "LEBANESE POUND": "LBP", LIRA: "LBP",
  // SYP: "SYP", "SYRIAN POUND": "SYP",
};
const normalizeCode = (v) => {
  if (!v) return "AED";
  const key = String(v).trim().toUpperCase();
  return NORMALIZE_MAP[key] || key;
};
const normalizeIso = (v) => (v ? String(v).trim().toUpperCase() : undefined);

/* choose the best flag for a row */
const flagFor = (code, iso) => {
  const isoKey = normalizeIso(iso);
  if (isoKey && ISO_FLAG_MAP[isoKey]) return ISO_FLAG_MAP[isoKey];
  const codeKey = normalizeCode(code);
  return CODE_FLAG_MAP[codeKey] || aeFlag;
};

// Base amounts in USD (default currency) - matching InvestmentTiers equity and returns exactly
const baseAmountsUSD = [
  { id: "a200m", valueUSD: 200000000, equity: 25, returns: 75, color: "#10b981" },
  { id: "a100m", valueUSD: 100000000, equity: 20, returns: 60, color: "#f59e0b" },
{ id: "a50m", valueUSD: 50000000, equity: 15, returns: 45, color: "#14b8a6" },
  { id: "a25m", valueUSD: 25000000, equity: 8, returns: 24, color: "#2563eb" },
  // { id: "a10m", valueUSD: 10000000, equity: 3, returns: 9, color: "#6366f1" },
];

const BANK_DETAILS = {
  USD: {
    "20%": {
      bank: "Emirates NBD 20%",
      iban: "AE610260000595735228203",
      name: "NOUREDDIN RAFIK ALHASSIBI",
      accountNumber: null,
      swift: "EBILAEAD",
      currency: "USD"
    },
    "80%": {
      bank: "ADCB 80%",
      iban: "AE160030014347023920002",
      name: "IWINTO LLC",
      accountNumber: null,
      swift: "ADCBAEAA",
      currency: "USD"
    }
  },
  AED: {
    "20%": {
      bank: "Emirates NBD 20% (AED)",
      iban: "AE330260001015735228201",
      name: "NOUREDDIN RAFIK ALHASSIBI",
      accountNumber: "1015735228201",
      swift: "EBILAEAD",
      currency: "AED"
    },
    "80%": {
      bank: "ADCB 80% (AED)",
      iban: "AE920030014347023910001",
      name: "IWINTO LLC",
      accountNumber: "14347023910001",
      swift: "ADCBAEAAXXX",
      currency: "AED"
    }
  }
};

const InvestmentCTA2 = () => {
  const { translations: t } = useLanguage();
  const { user } = useAuthListener();

  const [type, setType] = useState("normal");
  const [currency, setCurrency] = useState("USD");
  const [selectedId, setSelectedId] = useState("");

  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setIsProcessing] = useState(false); // retained for payment state

  // Sign-in popup state
  const [showSignInPopup, setShowSignInPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [, setIsSigningIn] = useState(false); // retained for signin state

  // Bank payment state
  const [showBankPayment, setShowBankPayment] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(''); // '20%' or '80%'

  // Dropdown state
  const [open, setOpen] = useState(false);
  const ddRef = useRef(null);

  useEffect(() => {
    const closeOnOutside = (e) => {
      if (!ddRef.current) return;
      if (!ddRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, []);

  // Fetch, normalize and de-dupe currencies with exchange rates
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const ref = collection(db, "app", "currencies", "available");
        const snap = await getDocs(ref);
  
        const raw = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        const normalized = raw.map((r) => {
          const code = normalizeCode(r.code || r.name || r.id || r.currency || r.symbol);
          const iso  = (r.iso || "").toString().trim().toUpperCase();
          const name = r.name || code;
          const exchangeRate = r.USDToCoinExchangeRate || 1; // Default to 1 if not found
          return { code, iso, name, exchangeRate };
        });
  
        // de-dupe by code
        const dedup = Array.from(new Map(normalized.map(c => [c.code, c])).values());
  
        // ⛔️ exclude Winis / Winicoins variants
        const EXCLUDE = new Set(["WINIS", "WINICOINS", "WINICOIN", "WCOIN"]);
        const cleaned = dedup.filter(c => !EXCLUDE.has(c.code.toUpperCase()));

        const ALLOWED_CODES = new Set(["USD", "AED"]);
        const filtered = cleaned.filter(c => ALLOWED_CODES.has(c.code));

        // order like your UI - USD first as base currency
        const ORDER = ["USD", "AED"];
        filtered.sort((a, b) => {
          const ai = ORDER.indexOf(a.code), bi = ORDER.indexOf(b.code);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });

        setCurrencies(filtered);

        // if current currency was excluded somehow, reset to USD or first available
        if (!filtered.find(c => c.code === currency) && filtered[0]) {
          const usdCurrency = filtered.find(c => c.code === "USD");
          setCurrency(usdCurrency ? "USD" : filtered[0].code);
        }
      } catch {
        // fallback without Winis - USD first as base currency
        const fallbackCurrencies = [
          { code: "USD", iso: "US", name: "US Dollar", exchangeRate: 1 },
          { code: "AED", iso: "AE", name: "UAE Dirham", exchangeRate: 3.67 }
        ];
        setCurrencies(fallbackCurrencies);
        if (!["USD", "AED"].includes(currency)) {
          setCurrency("USD");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCurrencies();
  }, [currency]);

  // Convert amounts based on selected currency
  const list = useMemo(() => {
    const selectedCurrency = currencies.find(c => c.code === currency);
    const usdCurrency = currencies.find(c => c.code === "USD");
    
    if (!selectedCurrency || !usdCurrency) {
      // If currencies not loaded yet, return USD amounts as default (sorted ascending)
      return baseAmountsUSD
        .map(item => ({
        ...item,
        value: item.valueUSD,
        originalValueUSD: item.valueUSD
        }))
        .sort((a, b) => a.value - b.value);
    }
    
    // Calculate conversion rate from USD to selected currency
    // USD to target currency = target exchange rate
    const usdToTargetRate = selectedCurrency.exchangeRate;
    
    return baseAmountsUSD
      .map(item => ({
        ...item,
        value: Math.round(item.valueUSD * usdToTargetRate),
        originalValueUSD: item.valueUSD
      }))
      .sort((a, b) => a.value - b.value);
  }, [currency, currencies]);

  const selected = list.find((x) => x.id === selectedId) || list[2];

  const title = t.investment?.readyTitle || "Ready to invest in iWinto?";
  const subtitle =
    t.investment?.readySubtitle ||
    "Join our journey and become part of the next big success story.";

  // Handle payment - process with Stripe
  const handlePayment = async (e, chosenItem = null) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    const currentUser = user || auth.currentUser;
    if (!currentUser) {
      setShowSignInPopup(true);
      return;
    }

    const selectedItem = chosenItem || list.find((x) => x.id === selectedId);
    if (!selectedItem) {
      alert('Please select an investment amount.');
      return;
    }

    setIsProcessing(true);
    
    try {
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Calculate 0.03% of the selected amount
      const downPaymentAmount = Math.round(selectedItem.value * 0.0003);
      const planTitle = `${selectedItem.value.toLocaleString()} ${currency} Investment - 0.03% Down Payment`;

      // Create checkout session for investment (0.03% down payment)
      const { sessionId } = await createCheckoutSession({
        amount: downPaymentAmount,
        planTitle: planTitle,
        duration: '1', // immediate payment
        multiplier: 1, // no multiplier for down payment
        currency: currency,
        customerEmail: currentUser.email,
        metadata: {
          userId: currentUser.uid,
          investorType: type === "normal" ? "Normal Investor" : "Equity Partner",
          fullInvestmentAmount: selectedItem.value,
          downPaymentAmount: downPaymentAmount,
          currency: currency,
          equity: selectedItem.equity,
          returns: selectedItem.returns,
          purchaseType: 'investment_down_payment'
        },
      });

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error('Stripe error:', error);
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Sign-in handlers
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setShowSignInPopup(false);
      setEmail('');
      setPassword('');
      setIsSigningIn(false);
      setTimeout(() => {
        handlePayment(null); // Pass null instead of no parameter
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithPopup(auth, googleProvider);
      setShowSignInPopup(false);
      setIsSigningIn(false);
      setTimeout(() => {
        handlePayment(null); // Pass null instead of no parameter
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const handleAppleLogin = async () => {
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithPopup(auth, appleProvider);
      setShowSignInPopup(false);
      setIsSigningIn(false);
      setTimeout(() => {
        handlePayment(null); // Pass null instead of no parameter
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const closeSignInPopup = () => {
    setShowSignInPopup(false);
    setEmail('');
    setPassword('');
    setAuthError('');
    setIsSigningIn(false);
  };

  // Bank payment handlers
  const handleCardSelect = (item) => {
    setSelectedId(item.id);
    setShowBankPayment(true);
    setSelectedPaymentType(''); // Reset payment type selection
  };

  const handleCopyIBAN = async (iban) => {
    if (!iban) return;
    try {
      await navigator.clipboard.writeText(iban);
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 3000);
    } catch (err) {
      console.error('Failed to copy IBAN:', err);
    }
  };

  const handleCopyAllDetails = async () => {
    const selectedBank =
      bankDetailsForCurrency?.[selectedPaymentType] || null;
    if (!selectedBank) return;

const detailsText = `${t.investment?.bankPayment?.accountNameLabel || "Account Name"}: ${selectedBank.name}
${t.investment?.bankPayment?.ibanLabel || "IBAN"}: ${selectedBank.iban}
${selectedBank.accountNumber ? `${t.investment?.bankPayment?.accountNumberLabel || "Account Number"}: ${selectedBank.accountNumber}\n` : ""}${t.investment?.bankPayment?.swiftLabel || "SWIFT Code"}: ${selectedBank.swift}
${t.investment?.bankPayment?.currencyLabel || "Currency"}: ${selectedBank.currency || currency}`;
    
    try {
      await navigator.clipboard.writeText(detailsText);
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 3000);
    } catch (err) {
      console.error('Failed to copy details:', err);
    }
  };

  const closeBankPayment = () => {
    setShowBankPayment(false);
    setSelectedPaymentType('');
  };

  const bankDetailsForCurrency = useMemo(
    () => BANK_DETAILS[currency] || BANK_DETAILS.USD,
    [currency]
  );
  const selectedBankDetails = useMemo(
    () =>
      selectedPaymentType
        ? bankDetailsForCurrency?.[selectedPaymentType] || null
        : null,
    [bankDetailsForCurrency, selectedPaymentType]
  );

  return (
    <section className="new-invest-form-section">
      <motion.h2 className="new-invest-form-title" initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:.45}}>
        {title}
      </motion.h2>
      <motion.p className="new-invest-form-subtitle" initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:.45,delay:.05}}>
        {subtitle}
      </motion.p>

      <form className="new-invest-card" onSubmit={handlePayment}>
        <div className="new-invest-form-inner">
          <div className="new-invest-row">
            <span className="new-invest-group-title">{t.investment?.form?.investmentType || "Investment Type *"}</span>
            <div className="new-invest-segmented">
              <button 
                type="button" 
                className={`new-invest-seg-item ${type==="normal" ? "new-active" : ""}`} 
                onClick={()=>setType("normal")}
                role="radio"
                aria-checked={type==="normal"}
              >
                <FaUser className="new-invest-seg-icon" /> 
                <span>{t.investment?.form?.normalInvestor || "Normal Investor"}</span>
              </button>
              <button 
                type="button" 
                className={`new-invest-seg-item ${type==="partner" ? "new-active" : ""}`} 
                onClick={()=>setType("partner")}
                role="radio"
                aria-checked={type==="partner"}
              >
                <FaUserTie className="new-invest-seg-icon" /> 
                <span>{t.investment?.form?.equityPartner || "Equity Partner"}</span>
              </button>
            </div>
          </div>

          {/* Currency dropdown */}
          <div className="new-invest-row">
            <span className="new-invest-group-title center">{t.investment?.form?.preferredCurrency || "Preferred Currency *"}</span>

            <div className="new-invest-currency-select-wrap">
              <div className="new-invest-currency-select" ref={ddRef}>
                <button
                  type="button"
                  className={`new-invest-currency-trigger ${open ? "open" : ""}`}
                  onClick={() => setOpen((o) => !o)}
                  aria-haspopup="listbox"
                  aria-expanded={open}
                >
                  <img
                    src={flagFor(currency, currencies.find(c=>c.code===currency)?.iso)}
                    alt={`${currency} flag`}
                    className="new-invest-currency-flag"
                    onError={(e) => (e.currentTarget.src = aeFlag)}
                  />
                  <span className="new-invest-currency-code">{currency}</span>
                </button>

                {!loading && open && (
                  <motion.div
                  className="new-invest-currency-menu"
                    role="listbox"
                    initial={{ opacity: 0, y: 6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.15 }}
                  >
                    {currencies.map((curr) => (
                      <button
                        type="button"
                        key={`${curr.code}-${curr.iso || "xx"}`}  // unique key
                      className="new-invest-currency-item"
                        role="option"
                        aria-selected={currency === curr.code}
                        onClick={() => { setCurrency(curr.code); setOpen(false); }}
                        title={curr.name || curr.code}
                      >
                        <img
                          src={flagFor(curr.code, curr.iso)}
                          alt={`${curr.code} flag`}
                        className="new-invest-currency-flag"
                          onError={(e) => (e.currentTarget.src = aeFlag)}
                        />
                        {/* <span className="invest-item-code">{curr.code}</span> */}
                      {curr.name && <span className="new-invest-item-name">{curr.name}</span>}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Amounts - horizontal scroll like ShortInvestmentPage */}
        <div className="new-invest-row">
          <span className="new-invest-group-title center">{t.investment?.form?.investmentAmount || "Investment Amount *"}</span>

          {/* Use ShortInvestmentPage scroll layout styles for full-bleed carousel while keeping our namespaced classes */}
          <div className="new-invest-amount-scroll-container short-term-plans-scroll-container">
            <div className="new-invest-amount-scroll short-term-plans-scroll">
              {list.map((item) => (
                <AmountCard
                  key={item.id}
                  item={item}
                  currency={currency}
                  active={selectedId===item.id}
                  onSelect={()=>handleCardSelect(item)}
                  onBuy={(it, e)=>{ setSelectedId(it.id); handlePayment(e, it); }}
                  t={t}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="new-invest-actions-inner">
          {/* <button 
            type="submit" 
            className="new-invest-btn new-primary"
            disabled={isProcessing || isSigningIn}
          >
            <FaCreditCard className="new-invest-btn-icon" />
            {isProcessing ? 'Processing...' : isSigningIn ? 'Signing in...' : !user ? 'Sign in to Pay' : (t.investment?.form?.payNowButton || "Pay Now")}
          </button>

          <div className="new-invest-divider"><span>{t.investment?.form?.orDivider || "OR"}</span></div> */}

          {/* <a href={waHref} target="_blank" rel="noreferrer" className="new-invest-btn new-whatsapp">
            <FaWhatsapp className="new-invest-btn-icon" />
            {t.investment?.form?.whatsappButton || "Discuss on WhatsApp"}
          </a> */}
        </div>
      </form>

      {/* Sign-in Popup */}
      {showSignInPopup && (
        <div className="new-signin-popup-overlay" onClick={closeSignInPopup}>
          <div className="new-signin-popup" onClick={(e) => e.stopPropagation()}>
            <div className="new-signin-popup-header">
              <h2>{t.investment?.signInPopup?.title || "Sign In to Continue"}</h2>
              <button className="new-signin-popup-close" onClick={closeSignInPopup}>
                ×
              </button>
            </div>
            
            <div className="new-signin-popup-content">
              <div className="new-signin-form-container">
                <div className="new-signin-form-wrapper">
                  <div className="new-signin-header">
                    <div className="new-signin-logo-border-radius">
                      <img src={require('../../assets/svgs/logo circular1.png')} alt="logo" />
                    </div>
                    <h1>{t.investment?.signInPopup?.welcome || "Welcome to iWinto"}</h1>
                  </div>

                  <form onSubmit={handleEmailLogin} className="new-signin-form">
                    <div className="new-form-group">
                      <input
                        type="email"
                        placeholder={t.investment?.signInPopup?.emailPlaceholder || "Email"}
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="new-form-group">
                      <input
                        type="password"
                        placeholder={t.investment?.signInPopup?.passwordPlaceholder || "Password"}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <button type="submit" className="new-signin-button">
                      {t.investment?.signInPopup?.loginButton || "Log in"}
                    </button>
                    {!!authError && <p className="new-signin-error">{authError}</p>}
                  </form>

                  <div className="new-signin-divider"><span>{t.investment?.signInPopup?.orContinueWith || "Or Continue With"}</span></div>
                  <div className="new-signin-social-buttons">
                    <button onClick={handleGoogleLogin} className="new-signin-google-button">
                      <img
                        src="https://www.google.com/favicon.ico"
                        alt="Google"
                        className="new-signin-google-icon"
                      />
                      Google
                    </button>
                    <button onClick={handleAppleLogin} className="new-signin-apple-button">
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg"
                        alt="Apple"
                        className="new-signin-apple-icon"
                      />
                      Apple
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bank Payment Section */}
      {showBankPayment && selected && (
        <div className="bank-payment-overlay" onClick={closeBankPayment}>
          <div className="bank-payment-container" onClick={(e) => e.stopPropagation()}>
            <div className="bank-payment-header">
              <h3>{t.investment?.bankPayment?.title || "Pay via Bank Transfer"}</h3>
              <button className="bank-payment-close" onClick={closeBankPayment}>
                ×
              </button>
            </div>
            
            <div className="bank-payment-content">
              <div className="selected-investment-info">
                <h4>{t.investment?.bankPayment?.selectedInvestment || "Selected Investment"}</h4>
                <div className="investment-amount">
                  {currency} {selected.value.toLocaleString()}
                </div>
              </div>

              <div className="payment-options">
                <h4>{t.investment?.bankPayment?.choosePaymentOption || "Choose Payment Option"}</h4>
                <div className="payment-buttons">
                  <button 
                    className={`payment-option-btn ${selectedPaymentType === '20%' ? 'active' : ''}`}
                    onClick={() => setSelectedPaymentType('20%')}
                  >
                    <span className="payment-percentage">20%</span>
                    <span className="payment-amount">
                      {currency} {Math.round(selected.value * 0.2).toLocaleString()}
                    </span>
                  </button>
                  <button 
                    className={`payment-option-btn ${selectedPaymentType === '80%' ? 'active' : ''}`}
                    onClick={() => setSelectedPaymentType('80%')}
                  >
                    <span className="payment-percentage">80%</span>
                    <span className="payment-amount">
                      {currency} {Math.round(selected.value * 0.8).toLocaleString()}
                    </span>
                  </button>
                </div>
              </div>

              {selectedPaymentType && selectedBankDetails && (
                <div className="bank-details-section">
                  <h4>{t.investment?.bankPayment?.detailsTitle || "Bank Transfer Details"}</h4>
                  <div className="bank-details-card">
                    <div className="bank-info">
                      <div className="bank-name">{selectedBankDetails.bank}</div>
                      <div className="bank-iban-section">
                        <label>{t.investment?.bankPayment?.ibanLabel || "IBAN"}:</label>
                        <div className="iban-container">
                          <span className="iban-text">{selectedBankDetails.iban}</span>
                          <button 
                            className="copy-iban-btn"
                            onClick={() => handleCopyIBAN(selectedBankDetails.iban)}
                            title={t.investment?.bankPayment?.copyIban || "Copy IBAN"}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                      {selectedBankDetails.accountNumber && (
                        <div className="bank-detail-row">
                          <label>{t.investment?.bankPayment?.accountNumberLabel || "Account Number"}:</label>
                          <span>{selectedBankDetails.accountNumber}</span>
                        </div>
                      )}
                      <div className="bank-detail-row">
                        <label>{t.investment?.bankPayment?.accountNameLabel || "Account Name"}:</label>
                        <span>{selectedBankDetails.name}</span>
                      </div>
                      <div className="bank-detail-row">
                        <label>{t.investment?.bankPayment?.swiftLabel || "SWIFT Code"}:</label>
                        <span>{selectedBankDetails.swift}</span>
                      </div>
                      <div className="bank-detail-row">
                        <label>{t.investment?.bankPayment?.currencyLabel || "Currency"}:</label>
                        <span>{selectedBankDetails.currency || currency}</span>
                      </div>
                      <div className="copy-all-details-section">
                        <button 
                          className="copy-all-details-btn"
                          onClick={handleCopyAllDetails}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                          {t.investment?.bankPayment?.copyAllDetails || "Copy All Details"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Copy Notification */}
      {showCopyNotification && (
        <div className="copy-notification">
          {t.investment?.bankPayment?.copiedNotification || "Details copied to clipboard!"}
        </div>
      )}
    </section>
  );
};

function AmountCard({ item, currency, active, onSelect, onBuy, large = false, t }) {
  // Set button color to match the circle color exactly
  const getButtonColor = () => {
    return item.color; // Use the same color as the circle
  };
  
  return (
    <motion.button
      type="button"
      onClick={onSelect}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      aria-pressed={active}
      className={`new-invest-amount-card ${active ? "new-is-active" : ""} ${large ? "new-is-large" : ""}`}
      style={{ 
        "--accent": getButtonColor(),
        "--border-accent": getButtonColor()
      }}
    >

      {/* Colored indicator per card */}
      <div className="new-invest-amount-ico" style={{ color: item.color }}>
        <svg
          className="new-invest-coin-stack"
          width="26" height="26" viewBox="0 0 24 24" fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.85"/>
          <circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.35"/>
        </svg>
      </div>

      <div className="new-invest-amount-number">
        <span className="new-invest-cur">{currency}</span> {item.value.toLocaleString()}
      </div>

      {/* <div className="new-invest-amount-meta">
        <span>
          <span className="new-equity-label">{t.investment?.form?.equityLabel || "Equity"}: {item.equity}%</span>
          <span className="new-equity-amount">{currency} {Math.round((item.value * item.equity) / 100).toLocaleString()}</span>
        </span>
        <span>
          <span className="new-return-label">{t.investment?.form?.returnLabel || "Return"}: {item.returns}%</span>
          <span className="new-return-amount">{currency} {Math.round((item.value * item.returns) / 100).toLocaleString()}</span>
        </span>
      </div> */}

      <div className="new-invest-amount-actions">
        {/* <button
          type="button"
          className="new-invest-buy-btn"
          style={{ 
            "--accent": getButtonColor(),
            backgroundColor: getButtonColor()
          }}
          onClick={(e)=>{ e.stopPropagation(); onBuy && onBuy(item, e); }}
        >
          <div className="new-invest-btn-content">
           
            <div className="new-invest-btn-bottom">
              {currency} {downPaymentAmount.toLocaleString()}
            </div>
            <div className="new-invest-btn-top">
              {t.investment?.form?.downPaymentBadge || "0.03% Down Payment"}
            </div>
          </div>
        </button> */}
      </div>
    </motion.button>
  );
}


export default InvestmentCTA2;
